import { CheckDiscountCodeForm, useCheckDiscountCodeMutation } from "@ac-expo/api";
import { EventDetails } from "@ac-expo/api-types";
import { BuyableEntryTypeWithKey, EntryType } from "@ac-expo/hooks";
import { getName } from "@ac-expo/utils";
import { BuyerInformationsForm } from "@web-app/src/schema/embedTicketing.schema";
import { UseFormReturn } from "react-hook-form";

type ActivityTicketSelected = {
  id: string;
  quantity: number;
};

export function getInformationsForConsent(
  event: EventDetails,
  activityTickets: ActivityTicketSelected[],
) {
  const structuresMapping = new Map<string, string>();
  const structures = event.eventStructures.map((eventStructure) => ({
    id: eventStructure.structure.id,
    name:
      eventStructure.structure.businessName ||
      getName({
        firstName: eventStructure.structure.admin?.firstName,
        lastName: eventStructure.structure.admin?.lastName,
      }),
    tickets: eventStructure.structure.activities.map((a) => a.ticketTypes).flat(),
  }));

  activityTickets.forEach((activityTicket) => {
    const structure = structures.find((s) => s.tickets.find((t) => t.id === activityTicket.id));

    if (structure && !structuresMapping.has(activityTicket.id)) {
      structuresMapping.set(structure.id, structure.name);
    }
  });

  const organiserId = event.organiserId;
  const organiserName =
    event.organiserName ||
    getName({
      firstName: event.organiser.admin.firstName,
      lastName: event.organiser.admin.lastName,
    }) ||
    "";
  const structureIds = Array.from(structuresMapping.keys());

  return {
    organiserId,
    organiserName,
    structureIds,
    structuresMapping,
  };
}

type ExportedItem = {
  id: string;
  quantity: number;
};

export function formatCartItemsForExport<T extends EntryType>(
  buyableEntries: BuyableEntryTypeWithKey<T>[],
  getTotalQuantity: (key: string) => number,
): ExportedItem[] {
  return buyableEntries
    .map((entry) => ({
      id: entry.entryType.id,
      quantity: getTotalQuantity(entry.itemKey),
    }))
    .filter((item) => item.quantity > 0);
}

export async function applyEmbeddedDiscountCode({
  mutation,
  discountCode,
  discountCodes,
  ticketsForExport,
  badgesForExport,
  transactionForm,
  discountCodeForm,
  setTotal,
  setTotalDiscount,
}: {
  mutation: ReturnType<typeof useCheckDiscountCodeMutation>;
  discountCode: string;
  discountCodes: string[] | undefined;
  ticketsForExport: ExportedItem[];
  badgesForExport: ExportedItem[];
  transactionForm: UseFormReturn<BuyerInformationsForm>;
  discountCodeForm?: UseFormReturn<CheckDiscountCodeForm>;
  setTotal: (price: number) => void;
  setTotalDiscount: (price: number) => void;
}) {
  await mutation.mutateAsync(
    {
      promotionCode: discountCode,
      currentUsedCodeNames: discountCodes ?? [],
      tickets: ticketsForExport,
      badges: badgesForExport,
    },
    {
      onError: (error) => {
        let message = "Une erreur est survenue, le code n'a pas pu être appliqué";
        if ("message" in error) {
          message = error.message as string;
        }
        discountCodeForm?.setError("discountCode", { message });
      },
      onSuccess: (result) => {
        if (discountCodeForm) {
          transactionForm.setValue("discountCodes", [
            ...(discountCodes ?? []),
            discountCodeForm.getValues().discountCode,
          ]);
          discountCodeForm.clearErrors("discountCode");
          discountCodeForm.setValue("discountCode", "");
        }

        setTotal(result.total);
        setTotalDiscount(result.totalDiscount);
      },
    },
  );
}
